if (!customElements.get('reviews-carousel')) {
  customElements.define('reviews-carousel', class extends HTMLElement {

    constructor() {
      super();
      this.currentIndex = 0;
      this.scrollTicking = false;
      this.isInitialized = false;
      this.isInitialLoad = true;
    }

    connectedCallback() {

      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('reviewsCarouselScrollY', window.scrollY);
      });

      const initCarousel = () => {

        this.container = this.querySelector('.reviews-carousel__track');
        this.slides = this.querySelectorAll('.reviews-carousel__slide');
        this.navItems = this.querySelectorAll('.reviews-carousel__nav-item');

        if (!this.container || this.slides.length === 0) {
          setTimeout(initCarousel, 100);
          return;
        }

        this.totalSlides = this.slides.length;

        this.init();

        /* 🔥 Force recalculation after page paint */
        requestAnimationFrame(() => {
          this.onResize();
          this.calculateProgress(true);
          this.updateSlides();
        });

        /* 🔥 Second safety recalculation */
        setTimeout(() => {
          this.onResize();
        }, 500);

        /* 🔥 Third safety recalculation (images/fonts/layout) */
        setTimeout(() => {
          this.onResize();
        }, 1000);
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCarousel);
        // Fallback for complete page load
        window.addEventListener('load', initCarousel);
      } else {
        initCarousel();
      }

      /* Shopify section reload support */
      document.addEventListener('shopify:section:load', () => {
        this.onResize();
      });

      // Disable initial load flag after all safety recalcs are likely done
      setTimeout(() => {
        this.isInitialLoad = false;
      }, 2000);
    }

    init() {

      if (this.isInitialized) return;
      if (this.totalSlides === 0) return;

      this.isInitialized = true;

      this.scrollHeightContainer = this.closest('.reviews-carousel__scroll-height');

      if (this.scrollHeightContainer) {
        this.updateContainerHeight();

        const savedScroll = sessionStorage.getItem('reviewsCarouselScrollY');
        if (savedScroll) {
          sessionStorage.removeItem('reviewsCarouselScrollY');
          // Wait for the new --scroll-height to expand the document before restoring scroll position
          setTimeout(() => {
            window.scrollTo({ top: parseInt(savedScroll, 10), behavior: 'instant' });
          }, 0);
        }
      }

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('load', this.onResize, { passive: true });

      /* ResizeObserver for layout changes */
      if (typeof ResizeObserver !== 'undefined') {

        this.resizeObserver = new ResizeObserver(() => {
          this.onResize();
        });

        if (this.scrollHeightContainer) {
          this.resizeObserver.observe(this.scrollHeightContainer);
        }

        if (this.slides.length > 0) {
          this.resizeObserver.observe(this.slides[0]);
        }
      }

      /* Intersection observer */
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.onResize();
          }
        });
      }, { threshold: 0.01 });

      this.intersectionObserver.observe(this);

      /* Nav click */
      this.navItems.forEach((item, index) => {
        item.addEventListener('click', () => {
          this.scrollToIndex(index);
        });
      });

      /* Slide click */
      this.slides.forEach((slide, index) => {
        slide.addEventListener('click', () => {
          this.scrollToIndex(index);
        });
      });

      /* Initial render */
      this.updateSlides();
    }

    disconnectedCallback() {

      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }

    onResize() {

      if (!this.scrollHeightContainer) return;

      const firstSlide = this.slides[0];
      const containerRect = this.scrollHeightContainer.getBoundingClientRect();

      // If dimensions aren't ready, we still want to force an initial layout
      // using our mobile/desktop fallback dimensions. We don't want to abort.
      if (!firstSlide || (firstSlide.offsetWidth === 0 && this.dimensionsRetryCount < 10)) {
        if (!this.dimensionsRetryCount) this.dimensionsRetryCount = 0;
        this.dimensionsRetryCount++;
        setTimeout(() => this.onResize(), 150);
        // Do not return here. Let the layout run with fallbacks so the user
        // sees a working slider immediately!
      } else {
        this.dimensionsRetryCount = 0;
      }

      requestAnimationFrame(() => {
        this.updateContainerHeight();
        this.calculateProgress(true);
        this.updateSlides();
      });
    }

    updateContainerHeight() {
      if (!this.scrollHeightContainer) return;

      const stickyWrapper = this.closest('.reviews-carousel__sticky-wrapper') || this.scrollHeightContainer.querySelector('.reviews-carousel__sticky-wrapper');
      if (!stickyWrapper) return;

      const isMobile = window.innerWidth < 750;
      // Distance in pixels to scroll for each slide transition.
      // Reduced significantly on mobile to avoid trapping the user inside a long scroll-linked animation
      const scrollDistancePerSlide = window.innerHeight * (isMobile ? 0.25 : 0.7);
      const totalScrollDistance = (this.totalSlides - 1) * scrollDistancePerSlide;

      // The total container height must be exactly the height of the sticky content 
      // plus the distance required to finish all transitions.
      const stickyHeight = stickyWrapper.offsetHeight;
      const totalHeight = stickyHeight + totalScrollDistance;

      this.scrollHeightContainer.style.setProperty('--scroll-height', `${totalHeight}px`);
    }

    onScroll() {

      if (!this.scrollHeightContainer) return;
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.calculateProgress(false);
        this.scrollTicking = false;
      });
    }

    calculateProgress(forceUpdate = false) {
      const rect = this.scrollHeightContainer.getBoundingClientRect();
      const stickyWrapper = this.scrollHeightContainer.querySelector('.reviews-carousel__sticky-wrapper');
      const stickyTopStr = stickyWrapper ? window.getComputedStyle(stickyWrapper).top : '0px';
      const stickyTop = parseInt(stickyTopStr) || 0;
      const stickyHeight = stickyWrapper ? stickyWrapper.offsetHeight : window.innerHeight;
      const scrollHeight = Math.max(1, rect.height - stickyHeight);
      const scrolled = -(rect.top - stickyTop);
      let progress = (scrolled / scrollHeight) * (this.totalSlides - 1);
      progress = Math.max(0, Math.min(progress, this.totalSlides - 1));

      if (this.currentContinuousProgress !== progress || forceUpdate) {
        this.currentContinuousProgress = progress;
        this.currentIndex = Math.round(progress);
        this.updateSlides();
      }
    }

    scrollToIndex(index) {

      if (index === this.currentIndex) return;
      if (!this.scrollHeightContainer) return;

      const rect = this.scrollHeightContainer.getBoundingClientRect();
      const stickyWrapper = this.scrollHeightContainer.querySelector('.reviews-carousel__sticky-wrapper');
      const stickyTopStr = stickyWrapper ? window.getComputedStyle(stickyWrapper).top : '0px';
      const stickyTop = parseInt(stickyTopStr) || 0;
      const stickyHeight = stickyWrapper ? stickyWrapper.offsetHeight : window.innerHeight;
      const scrollHeight = Math.max(1, rect.height - stickyHeight);
      const targetScroll = (index / (this.totalSlides - 1)) * scrollHeight;
      const absoluteTarget = window.pageYOffset + rect.top - stickyTop + targetScroll;

      window.scrollTo({
        top: absoluteTarget,
        behavior: 'smooth'
      });
    }

    updateSlides() {

      const isMobile = window.innerWidth < 750;

      const slideWidth = (this.slides.length > 0 && this.slides[0].offsetWidth > 0)
        ? this.slides[0].offsetWidth
        : (isMobile ? 260 : 320);

      const gap = isMobile ? 40 : 100;
      const xOffset = slideWidth + gap;
      const yOffsetBase = isMobile ? 80 : 250;
      const rotationAngle = 14;

      const activeProgress = this.currentContinuousProgress !== undefined
        ? this.currentContinuousProgress
        : this.currentIndex;

      this.slides.forEach((slide, index) => {

        let logicalOffset = index - activeProgress;

        // Implement infinite circular wrap-around so left side is never empty
        const halfSlides = this.totalSlides / 2;
        if (logicalOffset < -halfSlides) {
          logicalOffset += this.totalSlides;
        } else if (logicalOffset > halfSlides) {
          logicalOffset -= this.totalSlides;
        }

        const xPos = logicalOffset * xOffset;
        let absOffset = Math.abs(logicalOffset);
        
        // For a perfectly smooth arc over the apex, we use a power of 2 (parabola)
        // instead of 1.3 which creates a sharper cusp at the peak.
        const rotateZ = logicalOffset * rotationAngle;
        const scale = 1;
        const yPos = Math.pow(absOffset, 2) * yOffsetBase;

        const zIndex = Math.round(1000 - Math.abs(xPos));

        let opacity = 1;

        if (absOffset > 1.8) {
          opacity = 0;
          slide.style.pointerEvents = 'none';
        } else {
          slide.style.pointerEvents = 'auto';
        }

        slide.style.setProperty('--slide-x', `${xPos}px`);
        slide.style.setProperty('--slide-y', `${yPos}px`);
        slide.style.setProperty('--slide-scale', scale);
        slide.style.setProperty('--slide-rotate', `${rotateZ}deg`);
        slide.style.setProperty('--slide-z', zIndex);
        slide.style.setProperty('--slide-opacity', opacity);

        slide.classList.toggle('is-active', index === Math.round(activeProgress));
      });

      /* Update navigation */
      // Only scroll navigation into view if the index has changed to prevent performance issues
      if (this.lastScrolledIndex !== this.currentIndex) {
        this.lastScrolledIndex = this.currentIndex;
        
        this.navItems.forEach((item, index) => {
          const active = index === this.currentIndex;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active);

          if (active) {
            // Only scroll into view if not the initial page load to prevent jumping
            if (!this.isInitialLoad) {
              item.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest'
              });
            }
          }
        });
      }
    }

  });
}